import { create } from '@bufbuild/protobuf'
import _ from 'lodash'
import { useMemo } from 'react'

import { useGetInherentRisk } from '@/api/company.hook'
import {
  GetInherentRiskRequestSchema,
  InherentRiskGroupSchema,
  InherentRiskSource,
} from '@/gen/inventory/v1/company_service_pb'
import type { InherentRiskGroup } from '@/gen/inventory/v1/company_service_pb'

import { isDisplayedItem } from '@/pages/company-drawer/company-overview/displayed-inherent-risk-items'

export const useInherentRiskItems = (companyId: string, orgCompanyEngagementId?: string) => {
  const {
    data: inherentRiskGroups,
    categoryDisplayOrders,
    isLoading,
  } = useGetInherentRisk(
    create(GetInherentRiskRequestSchema, {
      companyId,
      orgCompanyEngagementId,
    }),
  )

  return {
    ...useSplitInherentRiskItems(inherentRiskGroups),
    categoryDisplayOrders,
    isLoading,
  }
}

export const useSplitInherentRiskItems = (inherentRiskGroups: InherentRiskGroup[]) => {
  const irqItems: InherentRiskGroup[] = useMemo(() => {
    return _.map(inherentRiskGroups, (group) => {
      return create(InherentRiskGroupSchema, {
        ...group,
        inherentRiskItems: _.filter(group.inherentRiskItems, (item) => {
          return item.source === InherentRiskSource.IRQ
        }),
      })
    })
  }, [inherentRiskGroups])

  const lemaItems: InherentRiskGroup[] = useMemo(() => {
    return _.map(inherentRiskGroups, (group) => {
      const irqItemIds = new Set(
        _.compact(
          group.inherentRiskItems
            .filter((item) => item.source === InherentRiskSource.IRQ)
            .map((item) => item.id),
        ),
      )
      return create(InherentRiskGroupSchema, {
        ...group,
        inherentRiskItems: _.filter(
          // Avoid showing non-displayed items, i.e., sub-processors with no "No" items
          group.inherentRiskItems.filter((item) => isDisplayedItem(item, group.categoryEnum)),
          (item) => item.source !== InherentRiskSource.IRQ && !(item.id && irqItemIds.has(item.id)),
        ),
      })
    })
  }, [inherentRiskGroups])

  return {
    irqItems,
    lemaItems,
  }
}

export type InherentRiskItemsData = ReturnType<typeof useInherentRiskItems>
